import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { paths } from "../routes/paths";
import APPOINTMENTS from "../api/services/APPOINTMENTS";
import BigCalendar from "../containers/_appointments/bigCalendar";
import CalendarSidebar from "../containers/_appointments/calendarSidebar";
import AppointmentDetailsModal from "../components/appointmentDetailsModal";
import ClassDetailsModal from "../components/classDetailsModal";
import ClientDetailsModal from "../components/clientDetailsModal";
import Layout from "../layouts/layout";
import CommentModal from "../components/commentModal";
import { calculateEndTimeOfAppointment } from "../utils/calculateEndTimeOfAppointment";
import { toast } from "react-toastify";
import { ConfirmModal } from "../components/confirmModal";
import EMPLOYEES from "../api/services/EMPLOYEES";
import styles from "../styles/_screens/appointments.module.scss";

// ── Employee color palette ──
const EMPLOYEE_COLORS = [
  "#DE1468", "#2563EB", "#16A34A", "#D97706",
  "#7C3AED", "#0891B2", "#DC2626", "#059669",
];

const Appointments = () => {
  const navigate = useNavigate();

  const token   = localStorage.getItem("token");
  let user_id   = localStorage.getItem("user_id");
  user_id = JSON.parse(user_id);

  const [appointments, setAppointments] = useState(null);
  const [appointmentsLoading, setAppointmentsLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [isCommentModalOpen, setIsCommentModalOpen] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [confirmModalLoading, setConfirmModalLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [employee, setEmployee] = useState("all");

  // Sidebar open/collapsed state (default open)
  const [sidebarOpen, setSidebarOpen] = useState(true);

  // Appointment details modal (opened from sidebar click)
  const [sidebarModalData, setSidebarModalData] = useState(null);
  // Class details modal (opened from sidebar click on CLASS type)
  const [classModalAppt, setClassModalAppt] = useState(null);
  // Client details modal
  const [clientModalData, setClientModalData] = useState(null);

  // redirect if no token
  useEffect(() => {
    if (!token) navigate(paths.login);
  }, [token]);

  // ── Build employeeColors map ──
  const employeeColors = useMemo(() => {
    const map = {};
    employees.forEach((emp, i) => {
      map[emp._id || emp.id] = EMPLOYEE_COLORS[i % EMPLOYEE_COLORS.length];
    });
    return map;
  }, [employees]);

  const calendarSummary = useMemo(() => {
    const now = new Date();
    const todayKey = now.toDateString();
    const all = appointments || [];
    const today = all.filter((appt) => new Date(appt.date).toDateString() === todayKey);
    const upcoming = all
      .map((appt) => {
        const date = new Date(appt.date);
        const [hours, minutes] = (appt.timeSlot || "00:00").split(":").map(Number);
        date.setHours(hours, minutes, 0, 0);
        return { ...appt, _calendarDate: date };
      })
      .filter((appt) => appt._calendarDate >= now)
      .sort((a, b) => a._calendarDate - b._calendarDate);

    return {
      todayCount: today.length,
      upcomingCount: upcoming.length,
      next: upcoming[0] || null,
    };
  }, [appointments]);

  // ── Fetch employees ──
  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await EMPLOYEES.get(user_id);
        setEmployees(res.data.data);
      } catch (error) {
        console.log(error);
      }
    };
    fetchData();
  }, []);

  // ── Fetch all appointments ──
  useEffect(() => {
    setSuccess(null);
    const fetchData = async () => {
      setAppointmentsLoading(true);
      try {
        const payload = employee === "all"
          ? { id: user_id }
          : { id: employee };

        const res = await APPOINTMENTS.get_appointments(payload);
        let arr = [];
        for (let i = 0; i < res.data.data.length; i++) {
          arr = [...arr, ...res.data.data[i].appoitnemts];
        }
        setAppointments(arr);
      } catch (error) {
        console.log(error);
      }
      setAppointmentsLoading(false);
    };
    fetchData();
  }, [success, employee]);

  // ── Handle sidebar appointment click ──
  const handleSidebarSelect = (appt) => {
    // CLASS type → open class details with participants
    if (appt.serviceId?.type === "CLASS") {
      setClassModalAppt(appt);
      setSidebarModalData(null);
      return;
    }

    // Regular appointment → transform for details modal
    const buildDate = (dateStr, timeSlot) => {
      const d = new Date(dateStr);
      const [h, m] = (timeSlot || "00:00").split(":").map(Number);
      d.setHours(h, m, 0, 0);
      return d;
    };

    const startDate = buildDate(appt.date, appt.timeSlot);
    const endTimeSlot = calculateEndTimeOfAppointment(appt.timeSlot, appt.serviceId?.time);
    const endDate = buildDate(appt.date, endTimeSlot);

    setClassModalAppt(null);
    setSidebarModalData({
      id:           appt._id,
      title:        appt.serviceId?.name  || "",
      userId:       appt.userId?._id      || appt.userId,
      customerName: appt.userId?.name     || "לקוח",
      phone:        appt.userId?.phone    || "",
      start:        startDate,
      end:          endDate,
      createdAt:    appt.createdAt        || new Date().toISOString(),
      type:         appt.serviceId?.type,
      employeeId:   appt.employeeId,
      serviceId:    appt.serviceId?._id,
    });
  };

  // ── Cancel appointment ──
  const cancelAppointment = async () => {
    const payload = { id: isConfirmModalOpen, status: 3 };
    setConfirmModalLoading(true);
    try {
      const res = await APPOINTMENTS.cancel_appointment(payload);
      setSuccess(true);
      setIsConfirmModalOpen(false);
      toast.success(res.data.message || "התור בוטל בהצלחה");
    } catch (error) {
      console.log(error);
      toast.error(error?.response?.data?.message || "שגיאה בביטול התור");
    }
    setConfirmModalLoading(false);
  };

  return (
    <Layout>
      <div className={styles.container}>
        <header className={styles.pageHeader}>
          <div className={styles.headingBlock}>
            <div className={styles.headingIcon} aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none">
                <path d="M7 3v3M17 3v3M4 9h16M6.5 13h3M6.5 17h3M14.5 13h3M14.5 17h3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                <rect x="3.5" y="4.5" width="17" height="16" rx="3" stroke="currentColor" strokeWidth="1.8" />
              </svg>
            </div>
            <div>
              <h1>יומן תורים</h1>
              <p>כל הלו״ז העסקי, הלקוחות והצוות במקום אחד</p>
            </div>
          </div>

          <div className={styles.summary} aria-label="סיכום היומן">
            <div className={styles.summaryItem}>
              <span className={styles.summaryValue}>{calendarSummary.todayCount}</span>
              <span className={styles.summaryLabel}>תורים היום</span>
            </div>
            <div className={styles.summaryDivider} />
            <div className={styles.summaryItem}>
              <span className={styles.summaryValue}>{calendarSummary.upcomingCount}</span>
              <span className={styles.summaryLabel}>תורים קרובים</span>
            </div>
            {calendarSummary.next && (
              <>
                <div className={styles.summaryDivider} />
                <div className={`${styles.summaryItem} ${styles.nextAppointment}`}>
                  <span className={styles.nextLabel}>התור הבא</span>
                  <strong>{calendarSummary.next.timeSlot}</strong>
                  <span>{calendarSummary.next.userId?.name || "לקוח"}</span>
                </div>
              </>
            )}
          </div>
        </header>

        <div className={styles.splitLayout}>
          {/* ── Main calendar (takes remaining width) ── */}
          <div className={styles.calendarMain}>
            <BigCalendar
              appointments={appointments}
              setSuccess={setSuccess}
              employees={employees}
              employee={employee}
              setEmployee={setEmployee}
              employeeColors={employeeColors}
              onSelectAppt={handleSidebarSelect}
              loading={appointmentsLoading}
            />
          </div>

          {/* ── Left sidebar — upcoming appointments ── */}
          <CalendarSidebar
            appointments={appointments}
            loading={appointmentsLoading}
            employeeColors={employeeColors}
            isOpen={sidebarOpen}
            onToggle={() => setSidebarOpen((v) => !v)}
            onSelectAppt={handleSidebarSelect}
          />
        </div>
      </div>

      {/* Appointment details opened from sidebar */}
      {sidebarModalData && (
        <AppointmentDetailsModal
          data={sidebarModalData}
          setIsOpen={() => setSidebarModalData(null)}
          setSuccess={setSuccess}
          onClientClick={(client) => {
            setSidebarModalData(null);
            setClientModalData(client);
          }}
        />
      )}

      {/* Class details opened from sidebar */}
      {classModalAppt && (
        <ClassDetailsModal
          appt={classModalAppt}
          setIsOpen={() => setClassModalAppt(null)}
          setSuccess={setSuccess}
          onClientClick={(client) => {
            setClassModalAppt(null);
            setClientModalData(client);
          }}
        />
      )}

      {/* Client details modal */}
      {clientModalData && (
        <ClientDetailsModal
          userId={clientModalData.userId}
          customerName={clientModalData.customerName}
          phone={clientModalData.phone}
          setIsOpen={() => setClientModalData(null)}
          setSuccess={setSuccess}
        />
      )}

      {isCommentModalOpen && (
        <CommentModal
          setIsOpen={setIsCommentModalOpen}
          appointmentId={isCommentModalOpen}
        />
      )}

      {isConfirmModalOpen && (
        <ConfirmModal
          setIsOpen={setIsConfirmModalOpen}
          handleClick={cancelAppointment}
          loading={confirmModalLoading}
        />
      )}
    </Layout>
  );
};

export default Appointments;
